import React from 'react';
import { Box, Typography, Container, Divider } from '@mui/material';

import { NAV_BG, AMBER } from '../theme';

const Section = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, mb: 1.5 }}>
            {title}
        </Typography>
        <Typography component="div" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.95rem', color: '#475569', lineHeight: 1.8 }}>
            {children}
        </Typography>
    </Box>
);

const PrivacyDriverPage = () => (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f1f5f9', py: 6 }}>
        <Container maxWidth="md">
            <Box sx={{ backgroundColor: 'white', borderRadius: '18px', border: '1px solid #e2e8f0', boxShadow: '0 8px 40px rgba(15,45,94,0.08)', p: { xs: 3, md: 6 } }}>

                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 800, fontSize: '2rem', color: NAV_BG, mb: 0.5 }}>
                        Privacy Policy
                    </Typography>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '1.1rem', color: AMBER, mb: 1 }}>
                        SBB Driver App
                    </Typography>
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>
                        Last updated: May 2026
                    </Typography>
                </Box>

                <Divider sx={{ mb: 4, borderColor: '#e2e8f0' }} />

                <Section title="1. Introduction">
                    SchoolByBus ("we", "our", or "us") operates the <strong>SBB Driver</strong> mobile application (app ID: com.sbnextgen.driver). This Privacy Policy describes what personal data we collect through the SBB Driver app, how we use it, and your rights under the General Data Protection Regulation (GDPR) and applicable Greek law.
                </Section>

                <Section title="2. Data Controller">
                    <strong>SchoolByBus</strong> is the data controller for personal data collected through the SBB Driver app.{'\n'}
                    Contact: <strong>sbnextgenapp@gmail.com</strong>
                </Section>

                <Section title="3. Data We Collect">
                    The SBB Driver app collects only the data necessary to operate the school bus service:{'\n\n'}
                    • <strong>Account credentials:</strong> Username and password (stored securely on your device using the OS keychain/keystore; never stored in plain text).{'\n'}
                    • <strong>GPS location:</strong> Your device's precise location while a route is active, including when the app is running in the background. Location data is transmitted to our servers and displayed to school administrators and to parents of students on your active route. We do not collect location data outside of active routes.{'\n'}
                    • <strong>Route & stop data:</strong> The route assigned to you, student names and pickup/drop-off points on your route, and pickup confirmation timestamps.{'\n'}
                    • <strong>Push notification token:</strong> An anonymised device token used to deliver operational alerts (route assignments, school messages). This token does not identify you personally.{'\n'}
                    • <strong>App usage data:</strong> Login timestamps and route start/end events for operational audit purposes.
                </Section>

                <Section title="4. Background Location">
                    The SBB Driver app requests the <strong>"Allow all the time"</strong> (background) location permission. This is required so that parents can track the bus in real time even when the driver locks their phone or switches to another app during an active route. Location data is only transmitted while you have an active route running in the app. You can stop location transmission at any time by ending the route in the app.
                </Section>

                <Section title="5. How We Use Your Data">
                    • Displaying your live position to school administrators and parents of students on your route{'\n'}
                    • Confirming student pickups and drop-offs{'\n'}
                    • Sending you operational push notifications{'\n'}
                    • Generating route completion reports for the school{'\n'}
                    • Maintaining account security
                </Section>

                <Section title="6. Legal Basis for Processing">
                    We process your data on the following legal bases:{'\n\n'}
                    • <strong>Performance of a contract</strong> — to provide the bus tracking and route management service you use as a driver.{'\n'}
                    • <strong>Legitimate interests</strong> — to ensure the safety of students during transport and to operate the service reliably.{'\n'}
                    • <strong>Consent</strong> — for push notifications, which you can withdraw at any time in your device settings.
                </Section>

                <Section title="7. Data Sharing">
                    We do not sell your data. Your location and route data is shared only with:{'\n\n'}
                    • School administrators responsible for your assigned routes{'\n'}
                    • Parents of students currently on your active route (live location only){'\n'}
                    • Infrastructure/hosting providers under strict data processing agreements{'\n'}
                    • Competent authorities when required by law
                </Section>

                <Section title="8. Data Retention">
                    • <strong>Live location:</strong> Stored for up to 30 days for operational review, then automatically deleted.{'\n'}
                    • <strong>Route records:</strong> Retained for the current academic year.{'\n'}
                    • <strong>Account data:</strong> Retained for the duration of your employment relationship with the school. You may request deletion at any time (see Section 10).
                </Section>

                <Section title="9. Data Security">
                    We protect your data with:{'\n\n'}
                    • HTTPS encryption for all data in transit{'\n'}
                    • JWT authentication with short-lived access tokens{'\n'}
                    • Credentials stored in the iOS Keychain / Android Keystore (not in plain storage){'\n'}
                    • Role-based access controls so only authorised users see your data
                </Section>

                <Section title="10. Your Rights (GDPR)">
                    As a data subject under GDPR you have the right to:{'\n\n'}
                    • <strong>Access</strong> a copy of your personal data{'\n'}
                    • <strong>Rectify</strong> inaccurate data{'\n'}
                    • <strong>Erase</strong> your data ("right to be forgotten"){'\n'}
                    • <strong>Restrict</strong> or <strong>object</strong> to processing{'\n'}
                    • <strong>Data portability</strong> — receive your data in a machine-readable format{'\n\n'}
                    To exercise any of these rights, email us at <strong>sbnextgenapp@gmail.com</strong>. We will respond within 30 days.
                </Section>

                <Section title="11. Children's Data">
                    The app displays the names and pickup locations of students assigned to your route. This data is provided by the school and is used solely to assist you in completing the route safely. You must not record, share, or use this data for any purpose outside the app.
                </Section>

                <Section title="12. Changes to This Policy">
                    We may update this policy to reflect changes in our practices or applicable law. Significant changes will be communicated through the app. Continued use of the SBB Driver app after an update constitutes acceptance of the revised policy.
                </Section>

                <Section title="13. Contact & Complaints">
                    <strong>SchoolByBus</strong>{'\n'}
                    Email: sbnextgenapp@gmail.com{'\n\n'}
                    You also have the right to lodge a complaint with the <strong>Hellenic Data Protection Authority (HDPA)</strong> at{' '}
                    <a href="https://www.dpa.gr" target="_blank" rel="noopener noreferrer" style={{ color: NAV_BG }}>www.dpa.gr</a>.
                </Section>

                <Divider sx={{ mb: 3, borderColor: '#e2e8f0' }} />

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <a href="/privacy" style={{ color: '#94a3b8', fontFamily: '"Outfit", sans-serif', fontSize: '0.9rem', textDecoration: 'none' }}>
                        ← General Privacy Policy
                    </a>
                </Box>
            </Box>
        </Container>
    </Box>
);

export default PrivacyDriverPage;
